/**
 * @file useBulkBillPlans.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-12
 * @modified 2026-10-12
 * @description Bulk Add Bills (2026-10-12): what Add Bill would make of each
 *              read bill in a batch (utils/bulkBillPlan) — with what Add Bill
 *              loads for it: the purchase items list, each quoted PO's lines
 *              (/api/bills/generate-from-pos, one bill per PO) and its billing
 *              context (/api/purchase-orders/billing-context: supplied
 *              quantities, what may change), the Xero lock and whether the
 *              user sees costs. `reloadPos()` loads the POs again (after a save
 *              marks some billed).
 *
 * 1.1.0 (review): `freshPlans(items)` loads those bills' POs afresh and
 *              answers their plans from that data — Accept asks about PO
 *              costs from what the POs are now, not from what was on screen.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import useXeroLock from './useXeroLock';
import { useAccess } from './useAccess';
import { planBulkBill, matchedPoId, isOpenBill } from '../utils/bulkBillPlan';

const json = (r) => r.json().catch(() => ({}));
const todayIso = () => new Date().toISOString().slice(0, 10);

const post = (url, body) => fetch(`${API_URL}${url}`, {
  method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
}).then(async r => ({ ok: r.ok, data: await json(r) }));

/** Each PO's bill lines (one bill per PO) and billing context. */
export async function fetchPoData(poIds) {
  const generated = {};
  if (!poIds.length) return { generated, context: {} };
  const { ok, data } = await post('/api/bills/generate-from-pos', { poIds, mode: 'separate' }).catch(() => ({ ok: false }));
  if (ok && data.success && Array.isArray(data.data)) {
    for (const b of data.data) for (const id of b.purchase_order_ids || []) generated[id] = b;
  } else {
    // One by one if any is refused.
    await Promise.all(poIds.map(async id => {
      const r = await post('/api/bills/generate-from-pos', { poIds: [id], mode: 'combined' }).catch(() => ({ ok: false }));
      generated[id] = r.ok && r.data?.success ? (Array.isArray(r.data.data) ? r.data.data[0] : r.data.data) : null;
    }));
  }
  for (const id of poIds) if (!(id in generated)) generated[id] = null;
  const ctx = await post('/api/purchase-orders/billing-context', { ids: poIds }).catch(() => ({ ok: false }));
  return { generated, context: ctx.ok && ctx.data?.success ? (ctx.data.pos || {}) : {} };
}

const poIdsOf = (items) => [...new Set((items || []).filter(isOpenBill).map(matchedPoId).filter(Boolean))].sort((a, b) => a - b);

export default function useBulkBillPlans(items) {
  const { firstAllowedDate } = useXeroLock();
  const { can } = useAccess();
  const seeCosts = can('costs.view');
  const [products, setProducts] = useState([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  // { [poId]: generated bill | null (failed) }; absent = loading.
  const [generatedByPo, setGeneratedByPo] = useState({});
  const [poContext, setPoContext] = useState({});
  const [poVersion, setPoVersion] = useState(0);

  useEffect(() => {
    let live = true;
    fetch(`${API_URL}/api/inventory/search?limit=10000&isPurchased=true&activeOnly=false`, { credentials: 'include' })
      .then(json)
      .then(d => { if (live) { setProducts(d.items || []); setProductsLoaded(true); } })
      .catch(() => { if (live) setProductsLoaded(true); });
    return () => { live = false; };
  }, []);

  const poIds = useMemo(() => poIdsOf(items), [items]);
  const poKey = poIds.join(',');

  useEffect(() => {
    if (!poIds.length) return undefined;
    let live = true;
    fetchPoData(poIds).then(({ generated, context }) => {
      if (!live) return;
      setGeneratedByPo(generated);
      setPoContext(context);
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poKey, poVersion]);

  const planAll = useCallback((list, generated, context) => {
    const map = new Map();
    const byId = new Map((list || []).map(i => [i.id, i]));
    for (const it of list || []) {
      map.set(it.id, planBulkBill(it, {
        products, generatedByPo: generated, poContext: context, firstAllowedDate, seeCosts, today: todayIso(),
        batchOriginal: it.batch_duplicate_of ? byId.get(it.batch_duplicate_of) || null : null,
      }));
    }
    return map;
  }, [products, firstAllowedDate, seeCosts]);

  const plans = useMemo(
    () => (productsLoaded ? planAll(items, generatedByPo, poContext) : new Map()),
    [items, productsLoaded, planAll, generatedByPo, poContext]
  );

  const reloadPos = useCallback(() => setPoVersion(v => v + 1), []);

  /** Plans for these items from their POs as they are now (loaded afresh). */
  const freshPlans = useCallback(async (list) => {
    const { generated, context } = await fetchPoData(poIdsOf(list));
    setGeneratedByPo(prev => ({ ...prev, ...generated }));
    setPoContext(prev => ({ ...prev, ...context }));
    return planAll(list, generated, context);
  }, [planAll]);

  return { plans, loading: !productsLoaded, reloadPos, freshPlans, firstAllowedDate };
}
